import { useEffect, useId, useState } from 'react';
import ActionLink from '../../../../Components/Dashboard/ActionLink';
import API from '../../../../Utils/api';
import Card from '../../../../Components/Dashboard/Card';
import Chips from '../../../../Components/Dashboard/Chips';
import Pill from '../../../../Components/Dashboard/Pill';
import { CardEmpty, CardError, CardLoading } from '../../../../Components/Dashboard/CardState';
import { formatDayMonth, formatTime } from '../../../../Utils/dates/formatDate';
import { hoursAgoText } from '../../../../Utils/dates/relativeDay';
import styles from '../adminCards.module.css';

const TITLE = 'Recent sign-ins';
// Labels say what the data proves (the last password sign-in or sign-up), not that anyone is online.
// "Active" is reserved for Account status in Users.
const LABEL = {
    within7d: 'Signed in within 7 days',
    d8to30: 'Signed in 8–30 days ago',
    over30d: 'Last sign-in over 30 days ago',
    none: 'No sign-in recorded',
    'no-login': 'Login not set up'
};
const TONE = { within7d: 'good', d8to30: 'warn', over30d: 'neutral', none: 'neutral', 'no-login': 'neutral' };
const FROM_ACCESS = { active: 'within7d', idle: 'd8to30', inactive: 'over30d', 'none-recorded': 'none', 'no-login': 'no-login' };
const bucketOf = row => row.signInBucket || FROM_ACCESS[row.accessStatus];
const HELP = 'Based on the last password sign-in or sign-up. Token refreshes are not counted. This does not show who is online.';
const RANGES = [['1d', 'Last 24 hours'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['all', 'All time']];
const ROLES = [['all', 'All roles'], ['Athlete', 'Athletes'], ['Coach', 'Coaches'], ['Admin', 'Admins']];

/** Admin-only. The label comes from the server's signInBucket (older APIs: accessStatus). */
const RecentSignInsCard = ({ now: nowProp }) => {
    const [range, setRange] = useState('7d');
    const [role, setRole] = useState('all');
    const [state, setState] = useState({ status: 'loading', items: [], counts: null, nextCursor: null, error: null });
    const [tick, setTick] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const helpId = useId();

    useEffect(() => {
        let cancelled = false;
        setState(s => ({ ...s, status: 'loading', error: null }));
        API.getAdminSignIns({ range, role, limit: 25 }).then(
            ({ data }) => !cancelled && setState({ status: 'success', items: data.items, counts: data.counts, nextCursor: data.nextCursor, error: null }),
            error => !cancelled && setState({ status: 'error', items: [], counts: null, nextCursor: null, error })
        );
        return () => {
            cancelled = true;
        };
    }, [range, role, tick]);

    const loadMore = async () => {
        setLoadingMore(true);
        try {
            const { data } = await API.getAdminSignIns({ range, role, limit: 25, cursor: state.nextCursor });
            setState(s => ({ ...s, items: [...s.items, ...data.items], counts: data.counts, nextCursor: data.nextCursor }));
        } catch (error) {
            setState(s => ({ ...s, status: 'error', error }));
        } finally {
            setLoadingMore(false);
        }
    };

    const now = nowProp || new Date();
    const { status, items, counts, nextCursor, error } = state;

    let body;
    if (status === 'error') body = <CardError label={TITLE} status={error?.response?.status} onRetry={() => setTick(t => t + 1)} />;
    else if (status === 'loading' && items.length === 0) body = <CardLoading label={TITLE} />;
    else if (items.length === 0) body = <CardEmpty>No sign-ins recorded in this range.</CardEmpty>;
    else
        body = (
            <>
                <div className={styles.cols} aria-hidden="true">
                    <span>User</span><span>Role</span><span>Last sign-in</span><span>Sign-in record</span>
                </div>
                <ul
                    className={styles.list}
                    tabIndex={0}
                    aria-label="Recent sign-ins list (password sign-in or sign-up; not live sessions)"
                    aria-describedby={helpId}
                >
                    {items.map(row => (
                        <li key={row.id}>
                            <div className={styles.row}>
                                <div className={styles.who}>
                                    <b>{row.firstName} {row.lastName}</b>
                                    <small>{row.email}</small>
                                </div>
                                <span>{row.role}</span>
                                <div className={styles.when}>
                                    {row.lastLoginAt ? (
                                        <>
                                            {`${formatDayMonth(row.lastLoginAt)}, ${formatTime(row.lastLoginAt)}`}
                                            <small>{hoursAgoText(row.lastLoginAt, now)}</small>
                                        </>
                                    ) : (
                                        <span>—</span>
                                    )}
                                </div>
                                <span data-testid="access-status">
                                    <Pill tone={TONE[bucketOf(row)]}>{LABEL[bucketOf(row)]}</Pill>
                                </span>
                            </div>
                        </li>
                    ))}
                </ul>
                {nextCursor && (
                    <div className={styles.more}>
                        <button type="button" className={styles.btn} disabled={loadingMore} onClick={loadMore}>
                            Load more
                        </button>
                    </div>
                )}
            </>
        );

    return (
        <Card
            id="si"
            module="admin-signins"
            title={TITLE}
            busy={status === 'loading'}
            flush
            right={<ActionLink to="/users">User Management</ActionLink>}
        >
            <div className={styles.toolbar}>
                <Chips
                    label="Time range"
                    value={range}
                    onChange={setRange}
                    options={RANGES.map(([key, label]) => ({ key, label, count: counts ? counts[key] : undefined }))}
                />
                <span className={styles.sep} aria-hidden="true" />
                <Chips label="Role" value={role} onChange={setRole} options={ROLES.map(([key, label]) => ({ key, label }))} />
            </div>
            {/* Visible copy (not a hover title) so keyboard and screen-reader users get the same caveat. */}
            <p id={helpId} className={styles.siHelp}>
                {HELP}
            </p>
            {body}
        </Card>
    );
};

export default RecentSignInsCard;
